import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Building2 } from "lucide-react";
import { useAuthStore } from "@/store/useAuthStore";
import { Header, type HeaderView } from "./components/Header";
import type { KpiScope } from "./components/kpiScope";
import {
  FilterToolbar,
  type Filters,
  type FilterKey,
} from "./components/FilterToolbar";
import {
  BreakdownExplorer,
  MetricBreadcrumb,
} from "./components/BreakdownExplorer";
import {
  MetricOverview,
  METRIC_LABELS,
  type MetricKey,
} from "./components/MetricOverview";
import { EngagementSection } from "./components/EngagementSection";
import { UsageMetricDetails } from "./components/UsageMetricDetails";
import {
  scopeStudents,
  metricsFor,
  pickDefaultDim,
  usedDims,
  teacherDominantSite,
  hashStr,
  type DrillSegment,
  type DimKey,
} from "./data/hierarchy";
import { AdoptionSection } from "./components/AdoptionSection";
import { ProficiencySection } from "./components/ProficiencySection";
import { DashboardSkeleton } from "./components/DashboardSkeleton";
import { Footer } from "./components/Footer";
import { EmptyState } from "./components/ui";
import {
  VARIANTS,
  variantForGroups,
  deriveVariant,
  gradeFraction,
  PERIOD_THROUGHPUT,
  PERIOD_REACH,
  CATEGORIES,
} from "./data/data";

export default function AdminAnalyticsDashboard({
  embedded = false,
}: {
  embedded?: boolean;
} = {}) {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const adminName = user?.name || "Admin";

  const [view, setView] = useState<HeaderView>("admin");
  const [filters, setFilters] = useState<Filters>({
    schools: [],
    teachers: [],
    groups: [],
    grades: [6, 7, 8],
    categories: [],
    dateRange: "This year",
    periodLabel: "This year",
  });
  const [categoryOptions] = useState<string[]>([...CATEGORIES]);

  // simulated data loading on cohort/period changes
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    const t = setTimeout(() => setLoading(false), 650);
    return () => clearTimeout(t);
  }, [filters.groups, filters.dateRange, filters.schools]);

  // ---- Drill-down hierarchy ------------------------------------------------
  const [drillPath, setDrillPath] = useState<DrillSegment[]>([]);
  const [breakdownDim, setBreakdownDim] = useState<DimKey>("teacher");
  const [selectedMetric, setSelectedMetric] = useState<MetricKey | null>(null);

  const scoped = useMemo(() => {
    const inPath = scopeStudents(drillPath);
    return inPath.filter((student) => {
      if (filters.schools.length > 0 && !filters.schools.includes(student.site))
        return false;
      if (
        filters.teachers.length > 0 &&
        !filters.teachers.includes(student.teacher)
      )
        return false;
      if (filters.grades.length > 0 && !filters.grades.includes(student.grade))
        return false;
      if (
        filters.categories.length > 0 &&
        !filters.categories.includes(student.category)
      )
        return false;
      return true;
    });
  }, [
    drillPath,
    filters.schools,
    filters.teachers,
    filters.grades,
    filters.categories,
  ]);
  const scopeMetrics = useMemo(() => metricsFor(scoped), [scoped]);
  const kpiScope = useMemo<KpiScope>(() => {
    const key = drillPath.map((p) => `${p.dim}:${p.value}`).join(">") || "root";
    return {
      activePct: scopeMetrics.activePct,
      activeCount: scopeMetrics.activeCount,
      lifetime: scopeMetrics.total,
      avgMin: scopeMetrics.avgMin,
      onTrackPct: scopeMetrics.onTrackPct,
      activeTrend: 1 + (hashStr(key + "a") % 6),
      avgTrend: 1 + (hashStr(key + "t") % 9),
      onTrackTrend: 1 + (hashStr(key + "o") % 5),
    };
  }, [drillPath, scopeMetrics]);

  const syncFiltersToPath = (path: DrillSegment[]) => {
    let schools: string[] = [];
    let teachers: string[] = [];
    let grades: number[] = [6, 7, 8];
    let categories: string[] = [];
    for (const seg of path) {
      if (seg.dim === "site") schools = [seg.value];
      if (seg.dim === "teacher") {
        teachers = [seg.value];
        if (schools.length === 0) schools = [teacherDominantSite(seg.value)];
      }
      if (seg.dim === "grade") grades = [Number(seg.value)];
      if (seg.dim === "category") categories = [seg.value];
    }
    setFilters((f) => ({ ...f, schools, teachers, grades, categories }));
  };

  const handleNavigate = (index: number) => {
    const next = index < 0 ? [] : drillPath.slice(0, index + 1);
    setDrillPath(next);
    setBreakdownDim((d) =>
      usedDims(next).includes(d) ? pickDefaultDim(next) : d,
    );
    syncFiltersToPath(next);
  };

  const handleMetricSelect = (metric: MetricKey) => {
    setSelectedMetric(metric);
    setDrillPath([]);
    setBreakdownDim("teacher");
  };

  const handleOverview = () => {
    setSelectedMetric(null);
    setDrillPath([]);
    setBreakdownDim("teacher");
  };

  const handleMetricRoot = () => {
    setDrillPath([]);
    setBreakdownDim("teacher");
    syncFiltersToPath([]);
  };

  const variant = VARIANTS[variantForGroups(filters.groups)];

  const visibleSchools = useMemo(() => {
    if (filters.schools.length === 0) return variant.schools;
    return variant.schools.filter((s) => filters.schools.includes(s.name));
  }, [variant, filters.schools]);

  const variantTotalStudents = useMemo(
    () => variant.schools.reduce((s, r) => s + r.students, 0),
    [variant],
  );
  const schoolStudents = visibleSchools.reduce((s, r) => s + r.students, 0);
  const hasData = filters.grades.length > 0 && visibleSchools.length > 0;

  const gradeFrac = gradeFraction(filters.grades);
  const allTeachersCount = variant.teachers.length;
  const teacherFrac =
    filters.teachers.length > 0
      ? Math.min(1, filters.teachers.length / allTeachersCount)
      : 1;
  const schoolFrac =
    variantTotalStudents > 0 ? schoolStudents / variantTotalStudents : 0;

  const studentsDisplayed = Math.round(
    schoolStudents * gradeFrac * teacherFrac,
  );
  const popMult =
    variantTotalStudents > 0 ? studentsDisplayed / variantTotalStudents : 0;
  const throughput = PERIOD_THROUGHPUT[filters.dateRange] ?? 1;
  const reachFactor = PERIOD_REACH[filters.dateRange] ?? 1;

  const dv = useMemo(
    () =>
      deriveVariant(variant, {
        popMult,
        throughputMult: popMult * throughput,
        reachFactor,
        teacherRowMult: schoolFrac * gradeFrac * throughput,
        studentsDisplayed,
        period: filters.dateRange,
        selectedTeachers: filters.teachers,
        selectedSites: filters.schools,
      }),
    [
      variant,
      popMult,
      throughput,
      reachFactor,
      schoolFrac,
      gradeFrac,
      studentsDisplayed,
      filters.dateRange,
      filters.teachers,
      filters.schools,
    ],
  );

  // effective render state, overridable by prototype controls
  const effLoading = loading;
  const effHasData = hasData;
  const sampleSize = studentsDisplayed;

  const hiddenFiltersForMetric: FilterKey[] =
    selectedMetric === "proficiency"
      ? ["site", "grade", "category", "period"]
      : [];

  return (
    <div
      className={`w-full font-mulish text-[#294B39] antialiased ${
        embedded ? "" : "min-h-screen bg-[#F3EDE2]"
      }`}
    >
      <div
        className={`mx-auto flex max-w-[1440px] flex-col ${
          embedded ? "p-0" : "p-6"
        } ${selectedMetric == null ? "gap-[18px]" : "gap-6"}`}
      >
        {!embedded && (
          <Header
            view={view}
            onView={setView}
            adminName={adminName}
            onExit={() => navigate("/classrooms")}
          />
        )}

        {view !== "admin" ? (
          <div className="flex min-h-[380px] flex-col items-center justify-center gap-4 rounded-[11px] border border-[#DED8CC] bg-[#FBF8F1] p-12 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-[11px] bg-[#28543D]/5 text-[#28543D]">
              <Building2 size={26} strokeWidth={2} />
            </div>
            <div className="text-lg font-bold text-[#294B39]">
              Analytics — teacher view
            </div>
            <div className="max-w-[440px] text-[13.5px] text-[#687368]">
              This teacher view isn't part of this dashboard. Choose{" "}
              <strong>Admin</strong> to open the district dashboard.
            </div>
            <button
              onClick={() => setView("admin")}
              className="mt-1 inline-flex cursor-pointer items-center gap-2 rounded-[6px] border-none bg-[#28543D] hover:bg-[#204833] px-[22px] py-[11px] text-sm font-bold text-white"
            >
              <Building2 size={16} strokeWidth={2.2} />
              Open Admin dashboard
            </button>
          </div>
        ) : effLoading ? (
          <DashboardSkeleton />
        ) : (
          <>
            {selectedMetric == null && (
              <MetricOverview
                scope={kpiScope}
                variant={dv}
                onSelect={handleMetricSelect}
              />
            )}

            {selectedMetric && (
              <FilterToolbar
                filters={filters}
                onChange={setFilters}
                categoryOptions={categoryOptions}
                hiddenFilters={hiddenFiltersForMetric}
                breadcrumb={
                  <MetricBreadcrumb
                    metricLabel={METRIC_LABELS[selectedMetric]}
                    path={drillPath}
                    onOverview={handleOverview}
                    onMetric={handleMetricRoot}
                    onNavigate={handleNavigate}
                  />
                }
              />
            )}

            {!effHasData ? (
              <EmptyState
                title="No data for this selection"
                message="No students match the current filters. Try selecting at least one grade and site, or reset your filters to see the full district."
                actionLabel="Reset filters"
                onAction={() => {
                  setDrillPath([]);
                  setBreakdownDim("teacher");
                  setFilters({
                    schools: [],
                    teachers: [],
                    groups: [],
                    grades: [6, 7, 8],
                    categories: [],
                    dateRange: filters.dateRange,
                    periodLabel: filters.periodLabel,
                  });
                }}
              />
            ) : selectedMetric == null ? null : (
              <>
                {selectedMetric === "active" && (
                  <>
                    <BreakdownExplorer
                      scoped={scoped}
                      path={drillPath}
                      dim={breakdownDim}
                      onDim={setBreakdownDim}
                    />
                    <AdoptionSection students={scoped} />
                  </>
                )}

                {selectedMetric === "time" && (
                  <EngagementSection
                    variant={dv}
                    grades={filters.grades}
                    schools={visibleSchools}
                    students={studentsDisplayed}
                  />
                )}

                {selectedMetric === "proficiency" && (
                  <ProficiencySection
                    variant={dv}
                    hasData={effHasData}
                    sampleSize={sampleSize}
                  />
                )}

                {(selectedMetric === "hours" ||
                  selectedMetric === "lessons" ||
                  selectedMetric === "teachers") && (
                  <UsageMetricDetails
                    metric={selectedMetric}
                    variant={dv}
                    students={scoped}
                  />
                )}
              </>
            )}
          </>
        )}

        <Footer />
      </div>
    </div>
  );
}
